import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { signIn } from 'aws-amplify/auth';
import { decodeJWT } from 'aws-amplify/auth';

import { customCookieService } from './cookie.service';

@Injectable({
  providedIn: 'root',
})
export class UAMAuthService {
  public loggedIn: BehaviorSubject<boolean>;
  idToken!: string;
  refreshToken!: string;
  deviceKey!: string;
  private tokenExpirationTimer?: ReturnType<typeof setTimeout>;
  private sessionExpiredTimeout?: ReturnType<typeof setTimeout>;
  private readonly TOKEN_REFRESH_THRESHOLD = 5 * 60;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _httpClient: HttpClient,
    private _snackBar: MatSnackBar,
    private cookieService: customCookieService
  ) {
    this.loggedIn = new BehaviorSubject<boolean>(false);
    this.getAuthTokens();
    if (this.idToken) {
      this.setupTokenRefresh();
    }
  }

  private normalizeAppHost(): string {
    return environment.url.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  }

  private sanitizeReturnPathForUam(
    routePath: string | undefined
  ): string | undefined {
    if (routePath == null) {
      return undefined;
    }
    const trimmed = routePath.trim();
    if (!trimmed) {
      return undefined;
    }
    const lower = trimmed.toLowerCase();
    if (lower.includes('/auth/login/') || /^https?:\/\//i.test(trimmed)) {
      return '/';
    }
    return trimmed;
  }

  buildUamLoginHref(routePath?: string): string {
    const app = this.normalizeAppHost();
    const safePath = this.sanitizeReturnPathForUam(routePath);
    const returnTarget =
      safePath && safePath !== '/'
        ? `${app}/${safePath.replace(/^\/+/, '')}`
        : app;
    return `${environment.uamUrl.trim()}auth/login/${returnTarget}`;
  }

  redirectToUamLogin(routePath?: string): void {
    const href = this.buildUamLoginHref(routePath);
    setTimeout(() => {
      this._snackBar
        .open('Session expired, login required.', 'OK', { duration: 5000 })
        .afterDismissed()
        .subscribe(() => {
          this.document.location.href = href;
        });
    }, 2000);
  }

  navigateToUamLogin(routePath?: string): void {
    this.document.location.href = this.buildUamLoginHref(routePath);
  }

  extractIdTokenFromRefreshResponse(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const r = response as Record<string, unknown>;
    const nested = r['response'] as Record<string, unknown> | undefined;
    const auth = nested?.['AuthenticationResult'] as
      | Record<string, unknown>
      | undefined;
    const fromNested = auth?.['IdToken'];
    if (typeof fromNested === 'string' && fromNested.length > 0) {
      return fromNested;
    }
    const flat = r['IdToken'];
    if (typeof flat === 'string' && flat.length > 0) {
      return flat;
    }
    return null;
  }

  private readExpFromToken(token: string): number | null {
    try {
      const decoded = decodeJWT(token);
      const exp = (decoded.payload as { exp?: number }).exp;
      return typeof exp === 'number' ? exp : null;
    } catch {
      return null;
    }
  }

  private setupTokenRefresh() {
    if (this.tokenExpirationTimer !== undefined) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = undefined;
    }

    const exp = this.idToken ? this.readExpFromToken(this.idToken) : null;
    if (exp == null) {
      return;
    }

    const expirationTime = exp * 1000;
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    const refreshTime =
      timeUntilExpiration - this.TOKEN_REFRESH_THRESHOLD * 1000;

    if (refreshTime > 0) {
      this.tokenExpirationTimer = setTimeout(() => {
        if (!this.refreshToken) {
          return;
        }
        this.refreshTokenFunction(this.refreshToken).subscribe({
          next: (response) => {
            const idToken = this.extractIdTokenFromRefreshResponse(response);
            if (idToken) {
              this.setIdToken(idToken);
            }
          },
          error: (error) => {
            console.error('Error refreshing token:', error);
            this.loggedIn.next(false);
          },
        });
      }, refreshTime);
    }
  }

  public login(username: string, password: string) {
    return signIn({ username, password });
  }

  public setIdToken(idToken: string) {
    if (idToken != null && idToken !== '') {
      this.idToken = idToken;
      this.checkTokenExpiration();
      this.setupTokenRefresh();
    }
  }

  public checkAuthentication(): Observable<boolean> {
    return new Observable((observer) => {
      this.getAuthTokens();
      if (this.checkTokenExpiration()) {
        observer.next(true);
        observer.complete();
      } else {
        observer.next(false);
        observer.complete();
      }
    });
  }

  public checkTokenExpiration(): boolean {
    if (!this.idToken) {
      this.loggedIn.next(false);
      return false;
    }

    const exp = this.readExpFromToken(this.idToken);
    if (exp == null) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiration = exp - currentTime;

    if (timeUntilExpiration < this.TOKEN_REFRESH_THRESHOLD) {
      this.refreshTokenFunction(this.refreshToken).subscribe({
        next: (response) => {
          const idToken = this.extractIdTokenFromRefreshResponse(response);
          if (idToken) {
            this.setIdToken(idToken);
          }
        },
        error: (error) => {
          console.error('Error refreshing token:', error);
          this.loggedIn.next(false);
        },
      });
    }

    if (exp < currentTime) {
      this.loggedIn.next(false);
      return false;
    }

    this.loggedIn.next(true);
    return true;
  }

  public decodeToken(token: string) {
    try {
      return decodeJWT(token);
    } catch (error) {
      console.error('Error decoding token:', error);
      this.sessionExpiredTimeout = setTimeout(() => {
        this._snackBar
          .open('Session expired, login required.', 'OK', { duration: 5000 })
          .afterDismissed()
          .subscribe(() => {
            this.document.location.href = this.buildUamLoginHref();
          });
      }, 2000);
      return null;
    }
  }

  public getAuthTokens() {
    this.idToken = this.cookieService.get('uam-id-token') || '';
    this.refreshToken = this.cookieService.get('uam-refresh-token') || '';
    this.deviceKey = this.cookieService.get('uam-device-key') || '';
  }

  public getIdToken() {
    return this.idToken;
  }

  public getRefreshToken() {
    return this.refreshToken;
  }

  public getDeviceKey(): string {
    return this.deviceKey;
  }

  public getAuthenticatedUser() {
    if (!this.idToken) {
      return null;
    }
    const decode = this.decodeToken(this.idToken);
    if (decode && decode.payload) {
      return decode.payload;
    }
    return {};
  }

  public refreshTokenFunction(refreshToken: string): Observable<unknown> {
    return this._httpClient.post<unknown>(`${environment.refreshEndpoint}`, {
      refreshToken,
      deviceKey: this.deviceKey,
    });
  }

  public clearSession() {
    if (this.tokenExpirationTimer !== undefined) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = undefined;
    }
    if (this.sessionExpiredTimeout !== undefined) {
      clearTimeout(this.sessionExpiredTimeout);
      this.sessionExpiredTimeout = undefined;
    }
    this.idToken = '';
    this.refreshToken = '';
    this.deviceKey = '';
    this.loggedIn.next(false);
  }
}
